import type { AxiosError, AxiosInstance, AxiosResponse, Method } from 'axios';
import axios from 'axios';

import settings from 'src/settings';
import i18n from 'src/services/i18n/i18next';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';

import type {
  HttpBaseRequest,
  HttpDeleteRequest,
  HttpGetRequest,
  HttpRequestWithMethod,
} from './types';

const REQUEST_METHOD: {
  GET: Method;
  POST: Method;
  PATCH: Method;
  PUT: Method;
  DELETE: Method;
} = {
  GET: 'GET',
  POST: 'POST',
  PATCH: 'PATCH',
  PUT: 'PUT',
  DELETE: 'DELETE',
};

/**
 * @class
 * @description This service class handles all REST Calls
 */
class HttpService {
  public service: AxiosInstance;

  /**
   * @function
   * @description Init the axios library and register the interceptors.
   */
  constructor() {
    this.service = axios.create({
      withCredentials: true,
      // Timeout in milliseconds until a request is abandoned.
      // Matches the async backend's gunicorn worker timeout from the backend.
      timeout: 120000,
    });

    // The AuthToken is set after login. If the user was already loaded on init, we need to set it.
    let token = localStorage.getItem('authToken');
    if (token) {
      this.setAuthToken(token, true);
    } else {
      token = sessionStorage.getItem('authToken');
      if (token) {
        this.setAuthToken(token);
      }
    }
  }

  /**
   * @description Creates the request headers and sets the accepted language for translated content.
   */
  getHeaders = (lang: string) => ({
    'Content-Type': 'application/json',
    'Accept-Language': lang || 'en',
  });

  /**
   * @function
   * @description Set the auth token in the request header. Call this after successful login.
   */
  setAuthToken = (token: string, stayLoggedIn = false) => {
    this.service.defaults.headers.common.Authorization = token
      ? `Token ${token}`
      : '';

    if (stayLoggedIn) {
      localStorage.setItem('authToken', token);
    } else {
      sessionStorage.setItem('authToken', token);
    }
  };

  /**
   * @function
   * @description Removes the auth token in the header. Call this after logout.
   */
  removeAuthToken = () => {
    delete this.service.defaults.headers.common.Authorization;
    localStorage.removeItem('authToken');
    sessionStorage.clear();
  };

  /**
   * @function
   * @description Handles unauthorized request, redirects to login
   */
  unauthorizedHandler = () => {
    this.removeAuthToken();
    if (window.location.pathname !== '/') {
      history.push(routePaths.LOGIN);
    }
    // Possibly routing to login
  };

  /**
   * @description Method to handle the request by method.
   */
  handleRequest: HttpRequestWithMethod = (
    method,
    path,
    data = {},
    params = {},
    noCache = false,
    requestConfig = {},
  ) =>
    this.service
      .request({
        ...requestConfig,
        url: `${settings.VITE_APP_API_URL}${path}`,
        data,
        method,
        params: { ...params, ts: noCache ? Date.now() : undefined } as Record<
          string,
          any
        >,
        headers: {
          ...this.getHeaders(i18n.language),
          ...requestConfig.headers,
        },
      })
      .catch(this.handleError);

  /**
   * @function
   * @description Interceptor for the error response
   */
  handleError = (error: AxiosError) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          this.unauthorizedHandler();
          break;
        default:
          break;
      }

      return Promise.reject(error);
    }

    // Possibly toaster or log error

    return Promise.reject(error);
  };

  handleFileResponse = (response: AxiosResponse) => {
    const filename: string | undefined = (
      response.headers?.['content-disposition'] ?? ''
    ).split('"')[1];
    const download = filename ?? 'download';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', download);
    document.body.append(link);

    try {
      link.click();
    } finally {
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  /**
   * @function
   * @description Fires the get request
   */
  get: HttpGetRequest = (path: string, ...restArgs) =>
    this.handleRequest(REQUEST_METHOD.GET, path, {}, ...restArgs);

  /**
   * @function
   * @description Fires a get request for a file and triggers the browser to save
   * the file when the download is complete
   */
  getFile = (path: string, params: object, noCache: boolean) =>
    this.service
      .request({
        method: REQUEST_METHOD.GET,
        responseType: 'blob',
        url: `${settings.VITE_APP_API_URL}${path}`,
        params: { ...params, ts: noCache ? Date.now() : null },
        headers: this.getHeaders(i18n.language),
      })
      .then(this.handleFileResponse);

  /**
   * @function
   * @description Fires a get request for an external file without headers and triggers the browser to save
   * the file when the download is complete
   */
  getExternalFile = (path: string, params: object, noCache: boolean) =>
    this.service
      .request({
        method: REQUEST_METHOD.GET,
        responseType: 'blob',
        url: path,
        params: { ...params, ts: noCache ? Date.now() : null },
      })
      .then(this.handleFileResponse);

  /**
   * @function
   * @description Fires the patch request
   */
  patch: HttpBaseRequest = (...args) =>
    this.handleRequest(REQUEST_METHOD.PATCH, ...args);

  /**
   * @function
   * @description Fires the put request
   */
  put: HttpBaseRequest = (...args) =>
    this.handleRequest(REQUEST_METHOD.PUT, ...args);

  /**
   * @function
   * @description Fires the put request
   */
  post: HttpBaseRequest = (...args) =>
    this.handleRequest(REQUEST_METHOD.POST, ...args);

  getFileWithPost = (path: string, data: any) =>
    this.service
      .request({
        method: REQUEST_METHOD.POST,
        responseType: 'blob',
        url: `${settings.VITE_APP_API_URL}${path}`,
        data,
        headers: this.getHeaders(i18n.language),
      })
      .then(this.handleFileResponse);

  /**
   * @function
   * @description Fires the put request
   */
  delete: HttpDeleteRequest = (...args) =>
    this.handleRequest(REQUEST_METHOD.DELETE, ...args);
}

export const storedAuthToken = () =>
  localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

export const HttpServiceInstance = new HttpService();

export default HttpServiceInstance;
